import _ from 'lodash'
import { getApiClient } from './ApiClient'

/**
 * Step 1 of admin login (FEA-309 / API-992).
 *
 * With `admin_mfa_required` on, a correct email + password no longer returns a token. It returns
 * `{ mfaRequired: true, challengeId }` and the app advances to the passcode screen. The
 * `if (!data.token)` branch below already tolerated a token-less response before this change, so the
 * transport did not need touching -- what changed is what the caller does with it.
 *
 * Resolves to either:
 *   { token, id, email, ... }                       -- flag off, single-step login
 *   { mfaRequired: true, challengeId, ... }         -- flag on, go to the passcode screen
 */
export const loginPromise = (email, password) => {
  return getApiClient()
    .post('/login', {
      body: {
        email: email,
        password: password,
      },
    })
    .then(resp => {
      const data = _.get(resp, 'data', {})
      if (!data.token) {
        // Hand back the data rather than the raw envelope: every caller wants `mfaRequired` and
        // `challengeId`, and returning `resp` made them reach through `resp.data` themselves.
        return Promise.resolve({ ...data })
      }
      return Promise.resolve({
        ...data,
      })
    })
}

/**
 * Step 2. The challengeId is the only handle the server accepts -- the email address is deliberately
 * not part of this request, so a code issued for one admin cannot be presented against another.
 */
export const verifyLoginCodePromise = (challengeId, code) => {
  return getApiClient()
    .post('/login/verify', {
      body: {
        challengeId,
        code,
      },
    })
    .then(resp => Promise.resolve({ ..._.get(resp, 'data', {}) }))
}

/**
 * Re-send the passcode for an existing challenge. Throttling is the server's job (API-992), not a
 * timer in the UI -- a client-side-only limit is not a limit.
 */
export const resendLoginCodePromise = challengeId => {
  return getApiClient()
    .post('/login/resend', {
      body: {
        challengeId,
      },
    })
    .then(resp => Promise.resolve({ ..._.get(resp, 'data', {}) }))
}

export const logoutPromise = () => {
  return getApiClient().post('/logout')
}
